/* eslint-disable no-restricted-syntax */
import {
  getInjectedPlugins,
  pipeInsertDataQuery,
  insertNode,
  ELEMENT_IMAGE,
} from '@udecode/plate';
import { getBlockAbove, getPluginType, getPluginOptions } from '@udecode/plate-common';
import {
  ELEMENT_TABLE,
  getCellTypes,
  getCellIndices,
  computeCellIndices,
} from '@udecode/plate-table';

import { getHeightAndWidthFromDataUrl } from 'utils/images';
import { createUploadStore } from './createUploadStore';
import { uploadFiles } from './uploadFiles';
import { renameFilesInFilelist } from './extractImage';

/**
 * Allows for pasting images from clipboard, uploading via drag n drop
 * and uploading via file explorer.
 */
export const withImageUpload = (
  editor,
  plugin,
) => {
  const { insertData } = editor;

  editor.insertData = (dataTransfer) => {
    const text = dataTransfer.getData('text/plain');
    const { files } = dataTransfer;

    if (!text && files && files.length > 0) {
      const injectedPlugins = getInjectedPlugins(editor, plugin);
      if (
        !pipeInsertDataQuery(injectedPlugins, {
          data: text,
          dataTransfer,
        })
      ) {
        return insertData(dataTransfer);
      }
      const renamedFiles = renameFilesInFilelist(files);
      uploadFiles(editor, renamedFiles);
    } else {
      insertData(dataTransfer);
    }
  };

  const uploadStore = createUploadStore({
    uploads: {},
  });

  editor.cloud = {
    uploadFiles: (files) => {
      uploadFiles(editor, files);
    },
    uploadStore,
    imageFileHandlers: {
      async onStart(img, filename, insert = true) {
        const { height, width } = await getHeightAndWidthFromDataUrl(img.url);
        // Add initial image node into slate editor.
        const node = {
          type: ELEMENT_IMAGE,
          // url: e.id,
          filename,
          bytes: img.size,
          width,
          height,
          maxWidth: width,
          maxHeight: height,
          children: [{ text: '' }],
        };

        // If in table cell, adjust width of image so it doesn't overflow table.
        const cellEntry = getBlockAbove(editor, {
          match: { type: getCellTypes(editor) },
        });
        if (cellEntry) {
          const [, cellPath] = cellEntry;
          const cell = cellEntry[0];
          const tableEntry = getBlockAbove(editor, {
            at: cellPath,
            match: { type: getPluginType(editor, ELEMENT_TABLE) },
          });

          if (tableEntry) {
            const [tableNode] = tableEntry;
            const { colSizes } = tableNode;
            const { _cellIndices } = getPluginOptions(editor, ELEMENT_TABLE);
            const { col } = getCellIndices(_cellIndices, cell) ||
              computeCellIndices(editor, tableNode, cell);

            // Subtract magic number twenty which is the combined left and right padding values.
            if (col) node.width = colSizes[col] - 20;
          }
        };


        if (insert) insertNode(editor, node);

        // Push file data to store to track upload progress in component.
        editor.cloud.uploadStore.set.upload(filename, {
          filename,
          status: 'progress',
          percent: 0.01,
          totalBytes: img.size,
        });
      },
      onProgress(filename, percent) {
        editor.cloud.uploadStore.set.upload(filename, {
          status: 'progress',
          percent,
        });
      },
      onError(filename, message) {
        const upload = {
          status: 'error',
          message,
        };
        if (message) {
          const topMessage = editor.errorHandler?.errorStore.get.top();
          if (!topMessage || topMessage.message !== message)
            editor.errorHandler?.pushErrorMessage(message);
        } else {
          editor.errorHandler?.pushErrorMessage(`
            Something went wrong with this image.
            Please try again.
          `);
        }
        editor.cloud.uploadStore.set.upload(filename, upload);
      },
      onSuccess(img, filename, url) {
        const upload = {
          status: 'success',
          url,
        };
        editor.cloud.uploadStore.set.upload(filename, upload);
      },
    },
  };

  return editor;
};
